import React, { useEffect, useState, useRef } from "react";
import { db } from "../firebase";
import { UserAuth } from "../context/AuthContext";
import HeaderComp from "./Components/HeaderComp";
import Footer from "./Components/Footer";
import { doc, getDoc } from "firebase/firestore";
import { Link } from "react-router-dom";
import { FaCoins, FaPaypal, FaArrowRight } from "react-icons/fa";
import { BsStars } from "react-icons/bs";
import { useLang } from "../i18n/LanguageContext";
import { formatPieces } from "../utils/pricing";
import { loadPaypal } from "../utils/paypal";
import CoinMoodChart from "./Components/CoinMoodChart";

const Money = () => {
  const { user, transferCoins, capturePaypalOrder } = UserAuth();
  const { t, lang } = useLang();
  // Les callbacks PayPal sont créés une seule fois : on lit la langue courante via une ref
  const tRef = useRef(t);
  tRef.current = t;
  const [balance, setBalance] = useState(null);
  const [amount, setAmount] = useState("50");
  // Acceptation des CGV avant tout achat de pièces
  const [accepted, setAccepted] = useState(false);
  const acceptedRef = useRef(accepted);
  acceptedRef.current = accepted;
  const [paypalFailed, setPaypalFailed] = useState(false);
  const [transferEmail, setTransferEmail] = useState("");
  const [transferAmount, setTransferAmount] = useState("");
  const [transferMsg, setTransferMsg] = useState(null);
  const [depositMsg, setDepositMsg] = useState(null);
  const [paypalReady, setPaypalReady] = useState(false);
  const paypalRef = useRef();
  // Toujours lire la valeur courante dans les callbacks PayPal (évite un montant figé)
  const amountRef = useRef(amount);
  amountRef.current = amount;
  const userRef = useRef(user);
  userRef.current = user;
  const capturePaypalOrderRef = useRef(capturePaypalOrder);
  capturePaypalOrderRef.current = capturePaypalOrder;

  const amountValid = Number(amount) >= 1 && Number(amount) <= 1000;

  // Charger le solde
  useEffect(() => {
    if (!user) return;
    const fetchBalance = async () => {
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) setBalance(Number(snap.data().balance) || 0);
    };
    fetchBalance();
  }, [user]);

  // PayPal : SDK chargé seulement ici (voir utils/paypal.js)
  useEffect(() => {
    let cancelled = false;
    loadPaypal()
      .then((paypal) => {
        if (cancelled || !paypalRef.current || paypalRef.current.hasChildNodes()) return;
        return renderButtons(paypal);
      })
      .catch(() => !cancelled && setPaypalFailed(true));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderButtons = (paypal) =>
    paypal
      .Buttons({
        createOrder: (data, actions) => {
          if (!acceptedRef.current) {
            setDepositMsg({ type: "error", text: tRef.current("Accepte d'abord les conditions générales.") });
            return Promise.reject(new Error("CGV non acceptées"));
          }
          const value = Number(amountRef.current);
          if (!(value >= 1 && value <= 1000)) {
            setDepositMsg({ type: "error", text: tRef.current("Montant invalide (entre 1 et 1000 €).") });
            return Promise.reject(new Error("Montant invalide"));
          }
          return actions.order.create({
            purchase_units: [
              {
                amount: { currency_code: "EUR", value: value.toFixed(2) },
                // Le serveur vérifie que la commande appartient bien à ce compte
                custom_id: userRef.current?.uid || "",
                description: "Pièces Tatie Soso",
              },
            ],
          });
        },
        onApprove: async (data) => {
          try {
            // Le serveur encaisse et vérifie la commande auprès de PayPal, puis crédite
            const r = await capturePaypalOrderRef.current(data.orderID);
            setBalance(r.balance);
            setDepositMsg({ type: "success", text: tRef.current("{n} pièces ajoutées à votre solde.", { n: r.amount }) });
          } catch (err) {
            console.error(err);
            setDepositMsg({ type: "error", text: tRef.current(err.message || "Le paiement n'a pas pu être enregistré. Contactez-nous.") });
          }
        },
        onError: (err) => {
          console.error("PayPal Error:", err);
          setDepositMsg({ type: "error", text: tRef.current("Erreur PayPal. Réessayez plus tard.") });
        },
      })
      .render(paypalRef.current)
      .then(() => setPaypalReady(true))
      .catch((e) => {
        console.error("PayPal render:", e);
        setPaypalFailed(true);
      });

  const handleTransfer = async () => {
    setTransferMsg(null);
    const num = parseFloat(transferAmount);
    const email = transferEmail.trim().toLowerCase();
    if (!email || isNaN(num) || num <= 0) {
      setTransferMsg({ type: "error", text: t("Email ou montant invalide.") });
      return;
    }
    if (email === (user.email || "").toLowerCase()) {
      setTransferMsg({ type: "error", text: t("Vous ne pouvez pas vous transférer des pièces à vous-même.") });
      return;
    }
    if (balance < num) {
      setTransferMsg({ type: "error", text: t("Solde insuffisant.") });
      return;
    }
    try {
      const r = await transferCoins(email, num);
      setBalance(r.balance);
      setTransferMsg({ type: "success", text: t("{n} pièces transférées à {email}.", { n: r.amount, email }) });
      setTransferEmail("");
      setTransferAmount("");
    } catch (e) {
      setTransferMsg({ type: "error", text: t(e.message) });
    }
  };

  const canPay = amountValid && accepted;

  return (
    <div className="min-h-screen bg-creme flex flex-col">
      <HeaderComp />

      {/* Hero */}
      <div className="bg-prune text-white py-10 px-6 text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <BsStars className="text-or" />
          <h1 className="text-3xl font-bold">{t("Mon Portefeuille")}</h1>
          <BsStars className="text-or" />
        </div>
        <p className="text-white/80 text-sm">{t("Gérez vos pièces d'or Tatie Soso")}</p>
      </div>

      <div className="max-w-5xl mx-auto w-full px-4 py-10 flex-1">
        <div className="grid md:grid-cols-2 gap-6">

          {/* ─── Solde ─── */}
          <div className="bg-white rounded-2xl shadow-carte p-6 flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-or rounded-full flex items-center justify-center">
                <FaCoins className="text-white text-xl" />
              </div>
              <div>
                <p className="text-xs text-stone-400 uppercase tracking-wider font-semibold">{t("Solde actuel")}</p>
                <p className="text-3xl font-bold text-prune">
                  {balance !== null ? formatPieces(balance, lang) : "..."}
                </p>
              </div>
            </div>
            {/* Valeur fixe : 1 pièce = 1 € (pas de cours fluctuant) */}
            <p className="text-sm text-stone-600">
              {formatPieces(1, lang)} = <span className="font-bold text-prune">1 €</span>
            </p>
            <CoinMoodChart />
            <p className="text-xs text-stone-600 leading-relaxed">
              {t("Tes pièces n'expirent pas. Celles que tu n'as pas utilisées te sont remboursées sur simple demande.")}{" "}
              <Link to="/cgv#pieces" className="text-prune underline">{t("En savoir plus")}</Link>
            </p>
          </div>

          {/* ─── Dépôt PayPal ─── */}
          <div className="bg-white rounded-2xl shadow-carte p-6 flex flex-col gap-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-[#0070ba] rounded-full flex items-center justify-center">
                <FaPaypal className="text-white text-xl" />
              </div>
              <div>
                <p className="text-sm font-bold text-stone-800">{t("Déposer des fonds")}</p>
                <p className="text-xs text-stone-400">{t("Via PayPal — 1 € = 1 pièce")}</p>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-stone-600 mb-1">{t("Montant (€)")}</label>
              <div className="flex gap-2 mb-3">
                {["10", "20", "50", "100"].map((v) => (
                  <button
                    key={v}
                    onClick={() => setAmount(v)}
                    className={`flex-1 py-2 rounded-lg text-sm font-semibold border-2 transition-all ${
                      amount === v
                        ? "border-prune bg-prune text-white"
                        : "border-stone-200 text-stone-600 hover:border-prune"
                    }`}
                  >
                    {v}€
                  </button>
                ))}
              </div>
              <input
                type="number"
                min={1}
                max={1000}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={t("Autre montant")}
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-prune mb-3"
              />
            </div>

            {!amountValid && (
              <p className="text-xs text-red-600">{t("Le montant doit être compris entre 1 et 1000 €.")}</p>
            )}

            {depositMsg && (
              <div className={`px-4 py-3 rounded-lg text-sm ${
                depositMsg.type === "success"
                  ? "bg-green-50 border border-green-200 text-green-700"
                  : "bg-red-50 border border-red-200 text-red-700"
              }`}>
                {depositMsg.type === "success" ? "✓" : "⚠️"} {depositMsg.text}
              </div>
            )}

            <label className="flex items-start gap-3 text-xs text-stone-700 bg-creme rounded-xl p-3 cursor-pointer">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-prune shrink-0"
              />
              <span>
                {t("J'accepte les")}{" "}
                <Link to="/cgv" target="_blank" className="text-prune underline">{t("conditions générales")}</Link>
                {t(" et je commande {n} pièces pour {n} €, avec obligation de paiement. Je dispose de 14 jours pour me rétracter.", { n: amountValid ? Number(amount) : "…" })}
              </span>
            </label>

            <div ref={paypalRef} className={canPay ? "" : "pointer-events-none opacity-50"} aria-disabled={!canPay} />

            {paypalFailed && !paypalReady && (
              <button
                disabled={!canPay}
                onClick={() => window.open("https://www.paypal.me/Tatiesoso", "_blank")}
                className="w-full py-3 bg-[#0070ba] text-white rounded-full font-semibold text-sm hover:bg-[#005ea6] transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaPaypal /> {t("Déposer {n}€ via PayPal", { n: amount })}
              </button>
            )}
          </div>

          {/* ─── Transfert ─── */}
          <div className="bg-white rounded-2xl shadow-carte p-6 md:col-span-2">
            <h3 className="font-bold text-stone-800 mb-4 flex items-center gap-2">
              <FaArrowRight className="text-prune" />
              {t("Transférer des pièces")}
            </h3>
            <p className="text-xs text-stone-600 -mt-2 mb-4">
              {t("Un transfert confirmé est définitif : vérifie bien l'adresse e-mail du destinataire.")}
            </p>

            <div className="grid sm:grid-cols-3 gap-3 items-end">
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-stone-600 mb-1">{t("Email du destinataire")}</label>
                <input
                  type="email"
                  value={transferEmail}
                  onChange={(e) => setTransferEmail(e.target.value)}
                  placeholder={t("exemple@email.com")}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-prune"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1">{t("Montant (pièces)")}</label>
                <input
                  type="number"
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                  placeholder="0"
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-prune"
                />
              </div>
            </div>

            {transferMsg && (
              <div className={`mt-3 px-4 py-3 rounded-lg text-sm ${
                transferMsg.type === "success"
                  ? "bg-green-50 border border-green-200 text-green-700"
                  : "bg-red-50 border border-red-200 text-red-700"
              }`}>
                {transferMsg.type === "success" ? "✓" : "⚠️"} {transferMsg.text}
              </div>
            )}

            <button
              onClick={handleTransfer}
              className="mt-4 px-8 py-2.5 bg-prune text-white rounded-full font-semibold text-sm hover:bg-prune-dark transition-colors"
            >
              {t("Transférer")}
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Money;
